import { Component, ErrorInfo, ReactNode } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { logger } from "../../utils/logger";
import i18n from "../../i18n/config";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

const t = (key: string): string =>
  String(i18n.t(`common:errorBoundary.${key}`));

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: "",
    };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return { hasError: true, error, errorInfo: null, errorId };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    logger.error("ErrorBoundary caught an error:", {
      error,
      errorInfo,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    if (process.env["NODE_ENV"] === "production") {
      this.sendErrorReport(error, errorInfo);
    }
  }

  private sendErrorReport = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      const errorReport = {
        errorId: this.state.errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: localStorage.getItem("userId") || "anonymous",
      };
      logger.debug("Error report prepared:", errorReport);
    } catch (reportError) {
      logger.error("Failed to send error report:", reportError);
    }
  };

  private handleReload = () => {
    this.handleRetry();
  };
  private handleGoHome = () => {
    window.location.href = "/";
  };
  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: "",
    });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { error, errorInfo, errorId } = this.state;
      const isDevelopment = process.env["NODE_ENV"] === "development";

      return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <ErrorIcon color="error" sx={{ fontSize: 48, mr: 2 }} />
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  color="error"
                  gutterBottom
                >
                  {t("title")}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("errorId")}: <code>{errorId}</code>
                </Typography>
              </Box>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                {t("description")}
              </Typography>
              {error && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontFamily: "monospace" }}
                >
                  {error.message}
                </Typography>
              )}
            </Alert>

            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                color="primary"
              >
                {t("retry")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
              >
                {t("reload")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
              >
                {t("home")}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {isDevelopment && error && errorInfo && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <BugIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{t("techInfo")}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("errorMessage")}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: "grey.50",
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {error.message}
                    </Paper>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("stackTrace")}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: "grey.50",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {error.stack}
                      </pre>
                    </Paper>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("componentStack")}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {errorInfo.componentStack}
                      </pre>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            <Box mt={3} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                💡 {t("help")} <code>{errorId}</code>
              </Typography>
            </Box>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
